import { format, parseISO, isValid } from 'date-fns'

export const formatDate = (dateString, formatStr = 'dd/MM/yyyy') => {
  if (!dateString) return '-'
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString
    return isValid(date) ? format(date, formatStr) : '-'
  } catch {
    return '-'
  }
}

export const formatDateTime = (dateString) => {
  return formatDate(dateString, 'dd/MM/yyyy h:mm a')
}

export const formatPrice = (price) => {
  if (!price) return '-'
  
  // If price is already formatted (contains $ or text)
  if (typeof price === 'string' && (price.includes('$') || /[a-zA-Z]/.test(price))) {
    return price
  }
  
  // Try to parse as number
  const num = parseFloat(price.replace(/[^0-9.]/g, ''))
  if (isNaN(num)) return price
  
  return new Intl.NumberFormat('en-NZ', {
    style: 'currency',
    currency: 'NZD',
    maximumFractionDigits: 0,
  }).format(num)
}

export const formatNumber = (num) => {
  if (num === null || num === undefined) return '-'
  return new Intl.NumberFormat('en-NZ').format(num)
}

export const formatArea = (area) => {
  if (!area) return '-'
  if (typeof area === 'string' && area.includes('m²')) return area
  return `${area} m²`
}

export const truncateText = (text, maxLength = 100) => {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength).trim() + '...'
}

// Updated color scheme: Blue theme with International Orange accents
export const getTypeColor = (type) => {
  const colors = {
    commercial_lease: 'bg-primary-100 text-primary-800 border border-primary-200',
    commercial_sale: 'bg-secondary-100 text-secondary-800 border border-secondary-200',
    residential_sale: 'bg-primary-100 text-primary-700 border border-primary-200',
    rural_sale: 'bg-accent-100 text-accent-800 border border-accent-200',
  }
  return colors[type] || 'bg-grey-100 text-grey-800 border border-grey-200'
}

export const getTypeLabel = (type) => {
  const labels = {
    commercial_lease: 'Commercial Lease',
    commercial_sale: 'Commercial Sale',
    residential_sale: 'Residential Sale',
    rural_sale: 'Rural Sale',
  }
  return labels[type] || type?.replace(/_/g, ' ') || 'Unknown'
}

// Updated status colors with blue and orange accents
export const getStatusColor = (status) => {
  const colors = {
    in_progress: 'bg-primary-100 text-primary-800 border border-primary-200',
    follow_up: 'bg-accent-100 text-accent-700 border border-accent-200',
    already_listed_with_jlr: 'bg-red-100 text-red-700 border border-red-200',
    archived: 'bg-grey-100 text-grey-600 border border-grey-200',
  }
  return colors[status] || 'bg-grey-100 text-grey-800 border border-grey-200'
}

export const getStatusLabel = (status) => {
  const labels = {
    in_progress: 'In Progress',
    follow_up: 'Follow Up',
    already_listed_with_jlr: 'Listed with JLR',
    archived: 'Archived',
  }
  return labels[status] || status?.replace(/_/g, ' ') || 'Unknown'
}

export const getCategoryColor = (category) => {
  const colors = {
    private_listing: 'bg-primary-100 text-primary-800',
    listing_with_address: 'bg-primary-100 text-primary-700',
    already_listed_with_jlr: 'bg-red-100 text-red-600',
    listing_without_address: 'bg-accent-100 text-accent-700',
  }
  return colors[category] || 'bg-grey-100 text-grey-800'
}

export const getInitials = (name) => {
  if (!name) return '?'
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2)
}

// New helper for days listed badge color
export const getDaysListedColor = (days) => {
  if (days === 0) return 'bg-accent-500 text-white'
  if (days <= 3) return 'bg-accent-100 text-accent-700 border border-accent-200'
  if (days <= 7) return 'bg-primary-100 text-primary-700 border border-primary-200'
  return 'bg-grey-100 text-grey-600 border border-grey-200'
}
